interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type CoalesceFn<TPayload> = (
  existing: TPayload,
  incoming: TPayload,
) => TPayload | null;

export interface SyncQueueOptions<TPayload> {
  storage: StorageLike;
  storageKey: string;
  // Called for each existing entry when a new one arrives. Return a merged
  // replacement to coalesce (the existing slot is overwritten and the new
  // entry is not appended), or null to leave the existing entry untouched
  // and keep scanning. If no entry coalesces, the new entry is appended.
  coalesce: CoalesceFn<TPayload>;
}

export class SyncQueue<TPayload> {
  private entries: TPayload[] = [];

  constructor(private readonly opts: SyncQueueOptions<TPayload>) {
    this.load();
  }

  size(): number {
    return this.entries.length;
  }

  peek(): TPayload | null {
    return this.entries[0] ?? null;
  }

  enqueue(entry: TPayload): void {
    for (let i = 0; i < this.entries.length; i += 1) {
      const merged = this.opts.coalesce(this.entries[i], entry);

      if (merged != null) {
        this.entries[i] = merged;
        this.persist();
        return;
      }
    }

    this.entries.push(entry);
    this.persist();
  }

  dequeue(): TPayload | null {
    const head = this.entries.shift() ?? null;

    if (head != null) {
      this.persist();
    }

    return head;
  }

  private load(): void {
    const raw = this.opts.storage.getItem(this.opts.storageKey);

    if (raw == null) {
      return;
    }

    try {
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) {
        this.entries = parsed;
      }
    } catch {
      this.entries = [];
    }
  }

  private persist(): void {
    if (this.entries.length === 0) {
      this.opts.storage.removeItem(this.opts.storageKey);
      return;
    }

    this.opts.storage.setItem(
      this.opts.storageKey,
      JSON.stringify(this.entries),
    );
  }
}
